import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Warehouse, Building2, Bike, Calculator, ChevronDown, BookOpen, Search, Printer } from "lucide-react";

const SECTIONS = [
  {
    role: "Warehouse Staff",
    icon: Warehouse,
    anyPerm: ["inventory.adjust"],
    intro: "Guides for running the central warehouse — receiving supplier stock, serving the branches, and your daily close entries.",
    guides: [
      {
        title: "Receiving stock from a supplier",
        pages: ["/inventory", "/procurement"],
        img: "/help/inventory.png",
        desc: "When a supplier delivers medicines, you record what arrived so the warehouse stock goes up and every batch and expiry date is tracked.",
        steps: [
          "Open Inventory → Stock & batches and click “Receive Stock”. (If the delivery is against a Purchase Order, open Procurement → Purchase orders and use “Receive goods” instead.)",
          "Pick the product. If you have a scanner, scan the pack’s barcode to select it automatically.",
          "Choose the warehouse as the Branch / Location.",
          "Type the Batch number exactly as printed on the box, and the Expiry date.",
          "Enter the Quantity received and the Unit cost (what you paid per pack).",
          "Add the supplier delivery note or invoice number in GRN / Reference.",
          "Click Receive. The stock is now available and the batch shows in the batch list.",
        ],
      },
      {
        title: "Fulfilling a branch stock request",
        pages: ["/transfers"],
        img: "/help/transfers.png",
        desc: "Branches send requests when they are low. You review the request and send the stock out from the warehouse to that branch.",
        steps: [
          "Open Inventory → Transfers. Requests waiting on you show as “Pending”.",
          "Click the request to see which medicines and quantities the branch asked for.",
          "Check you have enough stock. Adjust quantities if you can only send part.",
          "Approve the request to turn it into a transfer.",
          "Follow the “Processing a transfer out” guide below to send it.",
        ],
      },
      {
        title: "Processing a transfer out",
        pages: ["/transfers"],
        img: "/help/transfers.png",
        desc: "This moves stock out of the warehouse and hands it to a rider or branch. Stock leaves the warehouse the moment you dispatch.",
        steps: [
          "Open Inventory → Transfers and open the approved transfer.",
          "Confirm the medicines and quantities being sent are correct.",
          "Click “Dispatch”. The system picks the earliest-expiry batches first (FEFO) and reduces warehouse stock.",
          "Hand the goods to the rider or branch driver along with the printed transfer slip.",
          "The receiving branch will confirm arrival on their side — you’re done once dispatched.",
        ],
      },
      {
        title: "Daily stock adjustment for prescriptions",
        pages: ["/warehouse-daily"],
        img: "/help/warehouse-daily.png",
        desc: "An interim end-of-day entry (used until HMS is connected). You record the total units dispensed for prescriptions that day so warehouse stock is reduced correctly.",
        steps: [
          "Open Inventory → Warehouse daily and choose the Prescription Dispensing tab.",
          "Pick the warehouse and the date at the top.",
          "Click “New dispensing entry”.",
          "For each medicine, select the product and type the total units dispensed that day. Use “Add product” for more lines.",
          "Click “Submit for approval”. Stock is only reduced after a supervisor/accountant approves it.",
          "You can edit the entry while it is still Pending if you spot a mistake.",
        ],
      },
      {
        title: "Daily sales summary by payment type",
        pages: ["/warehouse-daily"],
        img: "/help/warehouse-daily.png",
        desc: "An interim end-of-day entry of how much you sold, split by how customers paid: cash, corporate credit, and insurance schemes.",
        steps: [
          "Open Inventory → Warehouse daily and choose the Sales Summary tab.",
          "Pick the warehouse and the date, then click “New sales summary”.",
          "Enter the total Cash sales value for the day.",
          "Under Credit, pick each corporate client and enter the value sold to them on account. Use “Add client” for more.",
          "Under Insurance, pick each scheme (e.g. NHIS) and enter the value billed to it. Use “Add scheme” for more.",
          "Enter “Total units sold today” — this is checked against the prescription dispensing entry.",
          "Click “Submit for approval”. A supervisor/accountant approves it. Edit while Pending if needed.",
        ],
      },
    ],
  },
  {
    role: "Branch Supervisor",
    icon: Building2,
    anyPerm: ["reports.branch"],
    intro: "Guides for keeping your branch stocked and running — requesting stock, receiving deliveries, and checking how your branch is doing.",
    guides: [
      {
        title: "Sending a stock request to the warehouse",
        pages: ["/transfers"],
        img: "/help/transfers.png",
        desc: "When your branch is running low on a medicine, you ask the warehouse to send more.",
        steps: [
          "Open Inventory → Transfers and click “New request”.",
          "Make sure your branch is selected as the destination and the warehouse as the source.",
          "Add each medicine you need and the quantity. Use “Add product” for more lines.",
          "Add a short note if something is urgent.",
          "Click Submit. The warehouse team will see your request and send the stock.",
          "Track the status here: Pending → Approved → Dispatched → Received.",
        ],
      },
      {
        title: "Receiving an incoming transfer",
        pages: ["/transfers"],
        img: "/help/transfers.png",
        desc: "When stock arrives from the warehouse (or another branch), you confirm what you actually received so your branch stock goes up.",
        steps: [
          "Open Inventory → Transfers. Transfers on the way to you show as “Dispatched / In transit”.",
          "Open the transfer and physically check the delivered boxes against the list.",
          "If everything matches, click “Receive”. Your branch stock increases automatically.",
          "If something is missing or damaged, note it before confirming and tell the warehouse.",
          "The transfer now shows as “Received” and the stock is ready to sell.",
        ],
      },
      {
        title: "Managing your branch inventory",
        pages: ["/inventory", "/near-expiry", "/ledger", "/stocktake"],
        img: "/help/inventory.png",
        desc: "Keep an eye on stock levels, expiry dates and batches at your branch.",
        steps: [
          "Open Inventory → Stock & batches to see every medicine and how many you have.",
          "Use the Alerts tab to spot items that are low or out of stock — these are good to request.",
          "Open Inventory → Near-expiry to find batches expiring soon so you can sell or return them first.",
          "Use the Movement ledger to see exactly how stock came in and went out.",
          "If a physical count is required, open Stocktaking and follow the blind-count sheet.",
        ],
      },
      {
        title: "Viewing branch reports",
        pages: ["/reports"],
        img: "/help/reports.png",
        desc: "See how your branch is performing — sales, stock and more — and export them if needed.",
        steps: [
          "Open Reports from the left menu.",
          "Choose the report you want (e.g. Sales, Inventory, Shift sessions).",
          "Set the date range at the top; your branch is applied automatically.",
          "Review the figures on screen.",
          "Click Export to download a CSV/Excel file to share or print.",
        ],
      },
    ],
  },
  {
    role: "Delivery",
    icon: Bike,
    anyPerm: ["delivery.view", "delivery.rider"],
    intro: "Guides for riders and delivery staff — see your assigned jobs and confirm pickups and drop-offs.",
    guides: [
      {
        title: "Viewing your assigned deliveries",
        pages: ["/deliveries", "/rider"],
        img: "/help/deliveries.png",
        desc: "See the deliveries and stock transfers that have been assigned to you.",
        steps: [
          "Open Delivery → My deliveries (riders) or Delivery → Deliveries.",
          "Your jobs are listed with the pickup point, destination and status.",
          "Tap a job to see the address, items and any customer notes.",
          "Jobs waiting to be picked up show as “Assigned” or “Ready”.",
        ],
      },
      {
        title: "Confirming dispatch (pickup)",
        pages: ["/deliveries", "/rider"],
        img: "/help/deliveries.png",
        desc: "When you collect the goods from the warehouse or branch, you confirm you have them so everyone knows they are on the way.",
        steps: [
          "Open the job in Delivery → My deliveries.",
          "Check the items you are collecting against the transfer slip.",
          "Tap “Confirm dispatch” / “Picked up”. The job moves to “In transit”.",
          "Head to the destination. The customer or branch can now track the delivery.",
        ],
      },
      {
        title: "Confirming delivery (drop-off)",
        pages: ["/deliveries", "/rider"],
        img: "/help/deliveries.png",
        desc: "When you hand the goods over at the destination, you confirm delivery to close the job.",
        steps: [
          "Open the in-transit job.",
          "Hand over the goods and check them with the receiver.",
          "Tap “Confirm delivery”. Capture proof (photo/signature) if the screen asks for it.",
          "The job is now marked “Delivered” and the receiving branch’s stock is confirmed.",
        ],
      },
    ],
  },
  {
    role: "Accountant",
    icon: Calculator,
    anyPerm: ["finance.statements", "reports.all", "adjustment.approve"],
    intro: "Guides for finance — review daily sales, approve stock entries, and pull the financial reports you need.",
    guides: [
      {
        title: "Viewing daily sales summaries by payment type",
        pages: ["/warehouse-daily"],
        img: "/help/warehouse-daily.png",
        desc: "Review each location’s end-of-day sales split by cash, corporate credit and insurance, and approve them.",
        steps: [
          "Open Inventory → Warehouse daily and choose the Sales Summary tab.",
          "Select the location and date to see that day’s entries.",
          "Click an entry to see the full breakdown: cash, each corporate client, and each insurance scheme, plus total units.",
          "If it looks correct, click Approve. If not, Reject with a reason so staff can fix it.",
          "For historical figures across dates, use Reports → Sales / Shift sessions and export.",
        ],
      },
      {
        title: "Viewing stock adjustment logs",
        pages: ["/adjustments", "/warehouse-daily"],
        img: "/help/adjustments.png",
        desc: "See and approve changes to stock — including the interim daily prescription dispensing entries — so inventory stays accurate.",
        steps: [
          "Open Inventory → Stock adjustments to see all adjustments and their status.",
          "Click any adjustment to see the products, quantities and the reason given.",
          "Approve genuine adjustments, or Reject ones that look wrong.",
          "For warehouse prescription dispensing, open Inventory → Warehouse daily → Prescription Dispensing and approve there (stock is deducted on approval).",
          "Every approval and rejection is recorded in the Audit trail for your records.",
        ],
      },
      {
        title: "Accessing financial reports",
        pages: ["/reports", "/statements", "/claims"],
        img: "/help/reports.png",
        desc: "Pull the numbers you need for reconciliation and management — sales, claims, statements and shift cash-ups.",
        steps: [
          "Open Reports from the left menu.",
          "Pick the dataset: Sales, Shift sessions (cash-ups & variances), Inventory, and more.",
          "Set the date range and, if needed, the branch filter.",
          "Review on screen, then Export to CSV/Excel.",
          "For corporate accounts, use Customers → Corporate statements to view balances and ageing.",
          "For insurance, use Insurance → Claims to review and batch claims.",
        ],
      },
    ],
  },
];

export const hasGuideFor = (path, can) =>
  SECTIONS.some((s) => s.anyPerm.some((p) => can(p)) && s.guides.some((g) => g.pages?.includes(path)));

function GuideCard({ g, forceOpen, highlight }) {
  const [open, setOpen] = useState(highlight);
  const isOpen = forceOpen || open;
  const slug = g.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <Card data-guide-highlight={highlight ? "true" : undefined} className={`shadow-sm overflow-hidden break-inside-avoid scroll-mt-20 transition-shadow ${highlight ? "ring-2 ring-primary" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`guide-toggle-${slug}`}
        className="w-full flex items-start justify-between gap-3 text-left px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <div>
          <div className="font-medium text-foreground">{g.title}</div>
          <div className="text-sm text-muted-foreground mt-0.5">{g.desc}</div>
        </div>
        <ChevronDown className={`help-noprint h-5 w-5 text-muted-foreground shrink-0 mt-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="border-t border-border pt-3 pb-4 px-4">
          <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground/90">
            {g.steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
          </ol>
          {g.img && (
            <img
              src={g.img}
              alt={`${g.title} — screen`}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="mt-3 rounded-lg border border-border w-full max-w-md shadow-sm"
            />
          )}
        </div>
      )}
    </Card>
  );
}

export default function Help() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [printMode, setPrintMode] = useState(false);
  const [params] = useSearchParams();
  const forPage = params.get("for") ? `/${params.get("for")}` : null;

  useEffect(() => {
    if (!forPage) return;
    const t = setTimeout(() => document.querySelector("[data-guide-highlight]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    return () => clearTimeout(t);
  }, [forPage]);

  const ql = q.trim().toLowerCase();
  const match = (g) => !ql || g.title.toLowerCase().includes(ql) || g.desc.toLowerCase().includes(ql) || g.steps.some((s) => s.toLowerCase().includes(ql));

  const visible = SECTIONS
    .filter((s) => s.anyPerm.some((p) => can(p)))
    .map((s) => ({ ...s, guides: s.guides.filter(match) }))
    .filter((s) => s.guides.length > 0);

  const printAll = () => {
    setPrintMode(true);
    setTimeout(() => { window.print(); setPrintMode(false); }, 150);
  };

  return (
    <div id="help-print" className="max-w-3xl">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #help-print, #help-print * { visibility: visible !important; }
        #help-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0 12px; }
        .help-noprint { display: none !important; }
        #help-print img { max-width: 420px; }
      }`}</style>

      <PageHeader title="Help & Guides" subtitle="Simple, step-by-step instructions for your everyday tasks." />

      <div className="help-noprint flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="help-search" className="pl-9" placeholder="Search a task, e.g. “transfer”, “sales summary”…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button variant="outline" data-testid="help-print-btn" onClick={printAll}><Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF</Button>
      </div>

      {visible.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
          {ql ? "No guides match your search." : "No guides are assigned to your role yet. Ask your administrator if you need help with a task."}
        </Card>
      ) : (
        <div className="space-y-8">
          {visible.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.role} data-testid={`help-section-${sec.role.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="h-4 w-4 text-primary" /></div>
                  <h2 className="text-lg font-semibold">{sec.role}</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{sec.intro}</p>
                <div className="space-y-2">
                  {sec.guides.map((g) => <GuideCard key={`${g.title}-${forPage}`} g={g} forceOpen={printMode || !!ql} highlight={!!forPage && !!g.pages?.includes(forPage)} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
